import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Nfc as NfcIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import NfcCardScanInput from "../../components/nfc/NfcCardScanInput";
import PageHeader from "../../components/layout/PageHeader";
import { NFCCard, nfcCardAPI } from "../../services/nfcCardAPI";
import { getApiErrorMessage } from "../../utils/apiError";
import { primaryGradient } from "../../theme";
import { useRBAC } from "../../hooks/useRBAC";

const formatInr = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const CheckBalancePage: React.FC = () => {
  const theme = useTheme();
  const { canRead } = useRBAC();

  const [scanBuffer, setScanBuffer] = useState("");
  const [scanSession, setScanSession] = useState(0);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState<NFCCard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetScan = useCallback(() => {
    setScanBuffer("");
    setCard(null);
    setError(null);
    setScanSession((s) => s + 1);
  }, []);

  const handleScanComplete = useCallback(
    async (cardNumber: string) => {
      const trimmed = cardNumber.trim();
      if (!trimmed || loading) return;

      setLoading(true);
      setError(null);
      setCard(null);

      try {
        const result = await nfcCardAPI.getCardByNumber(trimmed);
        setCard(result);
      } catch (err) {
        setError(getApiErrorMessage(err, "Card not found in system"));
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  if (!canRead("nfc_cards")) {
    return (
      <Box>
        <PageHeader
          title="Check Balance"
          subtitle="NFC card balance inquiry"
          breadcrumbs={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Check Balance" },
          ]}
        />
        <Alert severity="warning">
          Check balance is not available for your role.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Check Balance"
        subtitle="Tap customer card on reader to instantly check balance"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Check Balance" },
        ]}
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={resetScan}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Left — Tap NFC card */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "100%",
              minHeight: 360,
              borderRadius: 1,
              border: "2px solid",
              borderColor: alpha(theme.palette.primary.main, 0.4),
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: primaryGradient,
              }}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1" fontWeight={800}>
                Tap NFC card
              </Typography>
              <Chip
                icon={<NfcIcon />}
                label="Scanner active"
                color="primary"
                size="small"
                variant="outlined"
              />
            </Stack>

            {loading ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "primary.light",
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                }}
              >
                <CircularProgress size={48} thickness={4} sx={{ mb: 2 }} />
                <Typography variant="h6" fontWeight={800}>
                  Checking balance…
                </Typography>
              </Box>
            ) : (
              <Box sx={{ "& .MuiPaper-root": { borderRadius: 2 } }}>
                <NfcCardScanInput
                  key={scanSession}
                  value={scanBuffer}
                  onChange={setScanBuffer}
                  onScanComplete={handleScanComplete}
                  hideScannedValue
                  loading={loading}
                  disabled={loading}
                  label="NFC card"
                  hint="Tap card on USB reader"
                />
              </Box>
            )}

            <Alert
              severity="info"
              icon={<NfcIcon />}
              sx={{ mt: 2, borderRadius: 2 }}
              variant="outlined"
            >
              USB NFC reader se card tap karein
            </Alert>
          </Paper>
        </Grid>

        {/* Right — Balance */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "100%",
              minHeight: 360,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
              Balance
            </Typography>

            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <CircularProgress size={56} thickness={4} sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary" fontWeight={600}>
                  Balance load ho raha hai…
                </Typography>
              </Box>
            ) : card ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  py: 4,
                  px: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "success.light",
                  bgcolor: alpha(theme.palette.success.main, 0.06),
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Card {card.displayNumber}
                </Typography>
                {card.status !== "ACTIVE" && (
                  <Chip
                    label={card.status}
                    size="small"
                    color={card.status === "BLOCKED" ? "error" : "warning"}
                    sx={{ mt: 1, mb: 1, fontWeight: 700, alignSelf: "center" }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Available balance
                </Typography>
                <Typography
                  variant="h2"
                  fontWeight={800}
                  sx={{
                    mt: 0.5,
                    letterSpacing: "-0.02em",
                    color:
                      Number(card.balance) < 500 ? "warning.main" : "success.main",
                  }}
                >
                  {formatInr(card.balance)}
                </Typography>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={resetScan}
                  sx={{ mt: 3, alignSelf: "center" }}
                >
                  Scan another card
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <WalletIcon sx={{ fontSize: 64, color: "action.disabled", mb: 1.5 }} />
                <Typography variant="h6" fontWeight={700} color="text.secondary">
                  Balance yahan dikhega
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Left side se card tap karein
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CheckBalancePage;
